import React, { useEffect, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import RightPopUp from '../../components/common/RightPopUp';
import GenieMemoryConfigTable from './GenieMemoryConfigTable';
import { getItemFromMisc, getMySingleDoctorId, MISC_KEYS } from '../../store/idbHandler';
import pushGenie, { GenieMemoryConfig, GenieSettings } from '../../api/pushGenie';
import {
    DEFAULT_GENIE_MEMORY_CONFIG,
    DEFAULT_GENIE_SETTINGS,
    DEFAULT_INJECTIONS_MEMORY_CONFIG,
    DEFAULT_LAB_TESTS_MEMORY_CONFIG,
    DEFAULT_MEDICATIONS_MEMORY_CONFIG,
    GenieTableType,
    GenieVersion,
} from '../../constants';
import { notification } from 'antd';
import { captureError } from '../captureError';
import { track } from '../../tracking';
import { fetchMyGenie } from '../../store/actions/login';
import { useDispatch } from 'react-redux';
import { ItemId } from './GenieConfiguration';
import { SELECTED_TAB } from './GenieMemorySidebar';
import GenieCustomSectionMemoryConfig from './GenieCustomSectionMemoryConfig';

interface GenieMemoryProps {
    onClose: () => void;
    itemId: ItemId;
    toShow: SELECTED_TAB[];
}

const getTitle = (tab: SELECTED_TAB) => {
    switch (tab) {
        case SELECTED_TAB.LAB:
            return 'Lab Investigations';
        case SELECTED_TAB.MED:
            return 'Medications';
        case SELECTED_TAB.INJ:
            return 'Injections';
        case SELECTED_TAB.CUS:
            return 'Custom Sections';
        default:
            return '';
    }
};

export const GenieMemory: React.FC<GenieMemoryProps> = ({ onClose, itemId, toShow }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [initLoading, setInitLoading] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState<SELECTED_TAB>(toShow[0]);
    const [settings, setSettings] = useState<GenieSettings>(DEFAULT_GENIE_SETTINGS);
    const [genieMemoryConfig, setMemoryConfig] = useState<GenieMemoryConfig | undefined>(
        DEFAULT_GENIE_MEMORY_CONFIG,
    );
    const {
        medications,
        injections,
        labTests,
        isInjectionsEnabled,
        isLabTestEnabled,
        isMedicationsEnabled,
        customSections,
    } = genieMemoryConfig || {};

    const {padElements} = settings || [];

    const docId = getMySingleDoctorId() || '';

    const getGenieSettings = async (shouldLoad: boolean = false) => {
        try {
            if (shouldLoad) setInitLoading(true);
            const settings = await getItemFromMisc<GenieSettings>(docId, MISC_KEYS.settings);
            const { genieMemoryConfig } = settings;
            const ALLOWED_MED_FIELDS = DEFAULT_MEDICATIONS_MEMORY_CONFIG.map((med) => med.id);
            const ALLOWED_INJ_FIELDS = DEFAULT_INJECTIONS_MEMORY_CONFIG.map((inj) => inj.id);
            const ALLOWED_LAB_TEST_FIELDS = DEFAULT_LAB_TESTS_MEMORY_CONFIG.map((lt) => lt.id);
            let clone;

            if (genieMemoryConfig) {
                clone = {
                    ...genieMemoryConfig,
                    medications: (genieMemoryConfig?.medications || []).filter((med) =>
                        ALLOWED_MED_FIELDS.includes(med.id),
                    ),
                    injections: (genieMemoryConfig?.injections || []).filter((inj) =>
                        ALLOWED_INJ_FIELDS.includes(inj.id),
                    ),
                    labTests: (genieMemoryConfig?.labTests || []).filter((lt) =>
                        ALLOWED_LAB_TEST_FIELDS.includes(lt.id),
                    ),
                };
            } else {
                clone = DEFAULT_GENIE_MEMORY_CONFIG;
            }

            setMemoryConfig(clone);
            setSettings(settings);
        } catch (err) {
            setMemoryConfig(undefined);
            setSettings(DEFAULT_GENIE_SETTINGS);
        } finally {
            if (shouldLoad) setInitLoading(false);
        }
    };

    const onSave = async () => {
        try {
            setLoading(true);
            const { version } = await getItemFromMisc<GenieVersion>(docId, MISC_KEYS.genieVersion);
            await pushGenie(docId, {
                id: docId,
                version,
                settings: {
                    ...settings,
                    genieMemoryConfig: genieMemoryConfig || {},
                },
            });
            dispatch(fetchMyGenie(undefined, undefined, getGenieSettings));
            notification.success({
                message: 'Successfully saved!',
                duration: 5,
            });
            onClose();
        } catch (e) {
            console.error('pushGenie => ', e);
            alert('Unable to save changes, please try again');
            captureError(e as Error);
            track('ERROR', { on: 'saveGenie', what: (e as Error).message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getGenieSettings(true);
    }, []);

    return (
        <ReactHotkeys keyName="esc" onKeyDown={onClose}>
            <RightPopUp closeMe={onClose} width={'50%'} minWidth={'480px'}>
                <div className="bg-darwin-neutral-50 flex flex-col h-full space-between">
                    <div className="flex items-center space-x-16 p-16 cursor-pointer">
                        <div
                            onClick={onClose}
                            className="flex items-center justify-center w-16 h-16"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-darwin-neutral-1000"
                            >
                                <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                            </svg>
                        </div>
                        <p className="text-darwin-neutral-1000 heading-2 bold">Memory Settings</p>
                    </div>
                    {toShow.length > 1 && (
                        <div className="w-full pl-8 border-b border-neutral-8 select-none">
                            <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8 gap-8">
                                {toShow.map((tab) => {
                                    return (
                                        <div
                                            key={tab}
                                            className={
                                                selectedTab === tab
                                                    ? 'border-b-2 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                setSelectedTab(tab);
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === tab
                                                        ? 'pb-8 text-actionPrimary text-14 font-700'
                                                        : 'pb-8 text-neutral-3 text-14 font-700'
                                                }
                                            >
                                                {getTitle(tab)}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {selectedTab === SELECTED_TAB.MED && (
                        <GenieMemoryConfigTable
                            config={medications || DEFAULT_MEDICATIONS_MEMORY_CONFIG}
                            type={GenieTableType.MED}
                            updateConfig={setMemoryConfig}
                            isEnabled={Boolean(isMedicationsEnabled)}
                            loading={initLoading}
                        />
                    )}
                    {selectedTab === SELECTED_TAB.INJ && (
                        <GenieMemoryConfigTable
                            config={injections || DEFAULT_INJECTIONS_MEMORY_CONFIG}
                            type={GenieTableType.INJ}
                            updateConfig={setMemoryConfig}
                            isEnabled={Boolean(isInjectionsEnabled)}
                            loading={initLoading}
                        />
                    )}
                    {selectedTab === SELECTED_TAB.LAB && (
                        <GenieMemoryConfigTable
                            config={labTests || DEFAULT_LAB_TESTS_MEMORY_CONFIG}
                            type={GenieTableType.LAB}
                            updateConfig={setMemoryConfig}
                            isEnabled={Boolean(isLabTestEnabled)}
                            loading={initLoading}
                        />
                    )}
                    {selectedTab === SELECTED_TAB.CUS && (
                        <GenieCustomSectionMemoryConfig
                            config={customSections || []}
                            type={GenieTableType.CUS}
                            updateConfig={setMemoryConfig}
                            padElements={padElements}
                        />
                    )}
                    <div className="flex items-center justify-between px-16 py-8 space-x-8 border-t border-neutral-7">
                        <div>
                            <button onClick={onClose} className="neutral-btn btn--small px-16 py-8">
                                <span className="w-full text-center">Cancel</span>
                            </button>
                        </div>
                        <div>
                            <button
                                onClick={onSave}
                                className="primary-btn flex flex-row items-center justify-center  px-12 py-8 btn btn--small space-x-8"
                                disabled={loading}
                            >
                                {loading && (
                                    <div className="w-20 h-20 flex flex-row items-center justify-center">
                                        <svg className="circular-loader" viewBox="25 25 50 50">
                                            <circle
                                                className="loader-path"
                                                cx="50"
                                                cy="50"
                                                r="20"
                                                fill="none"
                                                stroke="#000000"
                                                stroke-width="3"
                                            ></circle>
                                        </svg>
                                    </div>
                                )}
                                <span className="w-full text-center">Save</span>
                            </button>
                        </div>
                    </div>
                </div>
            </RightPopUp>
        </ReactHotkeys>
    );
};

export default GenieMemory;
